import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { mavenceryLogoPink } from "assets";
import { MdOutlinePeopleAlt } from "react-icons/md";
import { FaPlus } from "react-icons/fa";
import { TbBellFilled } from "react-icons/tb";
import useScroll from "helpers/useScroll";
import { useDispatch, useSelector } from "react-redux";
import getNavigationPath from "utils/navigationHandler";
import NavItem from "./NavItem";
import OpenMenuButton from "./OpenMenuButton";
import { AddList } from "pages/Store/AddList";
import Friends from "pages/Friends/Friends";
import { backendBaseUrl } from "utils/axios";
import { io } from "socket.io-client";
import {
  updatePendingFriendRequestCount,
  updateUserNotificationCount,
} from "store/actions/Profile/profile";
import { showToast } from "utils/showToast";
import NofificationModal from "pages/Friends/components/NotificationModal";
import { getUserDashboard } from "store/services/Authentication";
import { getUnreadNotificationsCount } from "store/services/Notifications";
import { getPendingFriendRequestsCount } from "store/services/Friends/friends";
import {
  setActiveList,
  setList,
  setListItems,
  setLists,
} from "store/actions/Store/list";
import { getDashboard } from "store/actions/Authentication";
import Notifications from "pages/Notifications/Notifications";
import { MdOutlineKeyboardBackspace } from "react-icons/md";

const Navbar = ({ title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //state values from the store
  const isAuthenticated = useSelector(
    (state) => state.userReducer.isAuthenticated
  );
  const { user } = useSelector((store) => store.userReducer);
  const { selectedStore } = useSelector((store) => store.selectedStore);
  const { activeList } = useSelector((store) => store.storeLists);
  const { profile, notificationCount, friendRequestsCount } = useSelector(
    (store) => store.userProfile
  );
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  const navItems = useMemo(
    () => [
      {
        text: "Home",
        to: `/stores/${selectedStore?.id}/dashboard`,
        isActive: true,
      },
      {
        text: "Review List",
        to: `/stores/${selectedStore?.id}/lists/${activeList?.list_id}/reviewActiveList`,
        isActive: false,
      },
      {
        text: "Search",
        to: `/stores/${selectedStore?.id}/items`,
        isActive: false,
      },
      {
        text: "Profile",
        to: `/users/${user?.data?.id}/profile`,
        isActive: false,
      },
    ],
    [selectedStore, activeList, user]
  );
  //state variables
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [isScrollingUp, isScrollingDown] = useScroll();
  const [addListModalOpen, setAddListModalOpen] = useState(false);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [isNotificationBarOpen, setIsNotificationBarOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [, setUnreadNotificationsCount] = useState(0);
  const [, setFriendRequestsCount] = useState(0);
  const [modalContent, setModalContent] = useState("");
  const [navbarItems, setNavbarItems] = useState(navItems);

  useEffect(() => {
    const currentPath = location.pathname;
    let updatedNavItems = navItems.map((item) => ({
      ...item,
      isActive:
        currentPath.includes(item.to) ||
        (item.to.includes("review") && currentPath.includes("review")),
    }));

    setNavbarItems(updatedNavItems);
  }, [location, navItems]);

  const handleNavItemClick = (item) => {
    const updatedNavItems = navbarItems.map((navItem) => ({
      ...navItem,
      isActive: navItem.text === item.text,
    }));
    setNavbarItems(updatedNavItems);
    setIsNavbarOpen(false);
  };
  const handleClick = (title) => {
    const basePath = `/${location.pathname.split("/").slice(1, 3).join("/")}`;
    const path = getNavigationPath(
      title,
      basePath,
      user,
      selectedStore,
      isAuthenticated
    );
    navigate(path);
  };
  const isDashboardRoute =
    location.pathname === `/stores/${selectedStore?.id}/dashboard`;
  const handleAddlistModalToggle = () => {
    setAddListModalOpen(!addListModalOpen);
  };

  const handleMembersModalToggle = () => {
    setMembersModalOpen(!membersModalOpen);
  };

  const handleNotificationBarToggle = () => {
    setIsNotificationBarOpen(!isNotificationBarOpen);
  };
  const handleMessageModalToggle = () => {
    setIsMessageModalOpen(!isMessageModalOpen);
  };
  const handleLoginButton = () => {
    navigate("/auth?type=login");
  };
  const socket = useMemo(
    () =>
      io(backendBaseUrl, {
        transports: ["websocket", "polling", "flashsocket"],
        reconnection: true,
        rejectUnauthorized: false,
        query: {
          userId: user?.data.id,
          storeId: selectedStore?.id,
        },
      }),
    [selectedStore?.id, user]
  );
  useEffect(() => {
    socket.emit("message", new Date().getTime());

    socket.on("connect", () => {
      console.log("Connected ");
    });
    socket.on("unreadNotificationCount", ({ totalCount, userId }) => {
      setUnreadNotificationsCount(totalCount);
      if (userId === user.data.id) {
        dispatch(updateUserNotificationCount(totalCount));
      }
    });
    socket.on("newFriendNotification", ({ newFriend, userName }) => {
      if (
        newFriend &&
        newFriend.request_to === user.data.id &&
        !selectedStore?.isWhiteLabel
      ) {
        showToast(
          `You got new firend request from ${userName}`,
          "success",
          true
        );
      }
    });
    socket.on("noOfFriendRequests", ({ count, userId }) => {
      if (count && userId === user.data.id) {
        dispatch(updatePendingFriendRequestCount(count));
      }
    });
    socket.on("listSharedNotification", async (notification) => {
      if (notification && user.data.id === notification.userId) {
        const result = await getUserDashboard({
          store_id: selectedStore.id,
          user_id: user.data.id,
          token: user.accessToken,
        });
        if (result.data) {
          dispatch(setLists(result.data.lists));
          const activeList = result.data.lists.find((aL) => aL.active === true);
          dispatch(setActiveList(activeList));
          dispatch(setList(activeList));
          dispatch(setListItems(result.data.lists));
          dispatch(getDashboard(result.data));
        }
        setModalContent({
          title: "Notification",
          description: notification.message,
          storeName: notification.storeName,
          storeLogo: notification.image,
          redButton: "Okay",
        });
        setIsMessageModalOpen(true);
      }
    });

    socket.on("itemChangedInSharedList", async (notification) => {
      if (notification && user.data.id === notification.userId) {
        const result = await getUserDashboard({
          store_id: selectedStore.id,
          user_id: user.data.id,
          token: user.accessToken,
        });
        if (result.data) {
          dispatch(setLists(result.data.lists));
          const activeList = result.data.lists.find((aL) => aL.active === true);
          dispatch(setActiveList(activeList));
          dispatch(setList(activeList));
          dispatch(setListItems(result.data.lists));
          dispatch(getDashboard(result.data));
        }
        setModalContent({
          title: "Notification",
          description: notification.message,
          storeName: notification.storeName,
          storeLogo: notification.image,
          redButton: "Okay",
        });
        setIsMessageModalOpen(true);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [dispatch, selectedStore?.id, selectedStore?.isWhiteLabel, socket, user]);
  useEffect(() => {
    if (selectedStore && user) {
      const fetchData = async () => {
        try {
          const result = await getUserDashboard({
            store_id: selectedStore.id,
            user_id: user.data.id,
            token: user.accessToken,
          });
          const userNotificationsCount = await getUnreadNotificationsCount(
            user.accessToken,
            user.data.id,
            selectedStore.id
          );
          const countFriendRequests = await getPendingFriendRequestsCount(
            user.accessToken,
            user.data.id
          );
          if (countFriendRequests.success) {
            setFriendRequestsCount(countFriendRequests.data.totalCount);
            dispatch(
              updatePendingFriendRequestCount(
                countFriendRequests.data.totalCount
              )
            );
          }
          if (userNotificationsCount.success) {
            dispatch(
              updateUserNotificationCount(
                userNotificationsCount.data.totalCount
              )
            );
          }
          if (result.data) {
            dispatch(setLists(result.data.lists));
            const activeList = result.data.lists.find(
              (aL) => aL.active === true
            );
            dispatch(setActiveList(activeList));
            dispatch(setList(activeList));
            dispatch(setListItems(result.data.lists));
            dispatch(getDashboard(result.data));
          } else if (!result.success) {
            console.error("Error calling dashboard");
          }
        } catch (error) {
          console.error("Error calling dashboard:", error);
        }
      };
      fetchData();
    }
  }, [dispatch, selectedStore, user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isNotificationBarOpen &&
        !event.target.closest(".notification-icon")
      ) {
        setIsNotificationBarOpen(false);
      }
    };
    document.body.addEventListener("click", handleClickOutside);
    return () => {
      document.body.removeEventListener("click", handleClickOutside);
    };
  }, [isNotificationBarOpen]);
  return (
    <nav
      style={{
        boxShadow: "0px 0px 6px 0px #CDCACA",
      }}
      className={`bg-white border-gray-200 dark:bg-gray-900   w-full  fixed z-40 transition-transform duration-700 transform ${
        isScrollingDown ? "-translate-y-full" : ""
      }${isScrollingUp ? "translate-y-0" : ""}`}
    >
      <div
        className={`flex flex-wrap items-center justify-between  mx-auto lg:px-16 max-w-full-s ${
          selectedStore?.isWhiteLabel && isAuthenticated === null
            ? "px-4 py-2"
            : "p-4"
        }`}
      >
        <div className="flex items-center justify-center space-x-3 rtl:space-x-reverse">
          {title !== "" ? (
            <div className="flex w-32 gap-2 text-xl font-semibold text-gray-700 hover:text-blue-700 align-center whitespace-nowrap">
              <div
                onClick={() => handleClick(title)}
                className={`mt-1 ${
                  isDashboardRoute || isAuthenticated === null ? "hidden" : ""
                }`}
              >
                <MdOutlineKeyboardBackspace />
              </div>
              <div className="">
                {title && title === "Hello," ? (
                  profile.first_name && profile.first_name !== null ? (
                    <p
                      onClick={() => handleClick(title)}
                      className="font-bold cursor-pointer hover:text-gray-700 "
                    >
                      <span>Hello, {profile.first_name} </span>
                      <span
                        className={`${
                          profile.first_name.length >= 15 ? "lg:hidden" : ""
                        } hidden md:hidden lg:inline  `}
                      >
                        {profile.last_name}
                      </span>
                    </p>
                  ) : (
                    <span
                      onClick={() => handleClick(title)}
                      className="cursor-pointer"
                    >
                      Hello,
                    </span>
                  )
                ) : isAuthenticated === null ? (
                  <img
                    onClick={() =>
                      navigate(
                        selectedStore?.isWhiteLabel
                          ? `/${selectedStore?.id}`
                          : "/"
                      )
                    }
                    src={
                      selectedStore?.isWhiteLabel
                        ? selectedStore.logo
                        : mavenceryLogoPink
                    }
                    className={`object-cover ${
                      selectedStore?.isWhiteLabel ? "h-12 w-12 p-0" : "h-8"
                    }`}
                    alt="MavenceryLogo"
                  />
                ) : (
                  <span
                    onClick={() => handleClick(title)}
                    className="cursor-pointer"
                  >
                    {title}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <img
              src={
                selectedStore?.isWhiteLabel
                  ? selectedStore.logo
                  : mavenceryLogoPink
              }
              className={`object-cover ${
                selectedStore?.isWhiteLabel ? "h-12  w-12 p-0" : "h-8"
              }`}
              alt="MavenceryLogo"
            />
          )}
        </div>
        <div className="flex items-center gap-4 space-x-3 md:order-2 md:space-x-0 rtl:space-x-reverse">
          {isAuthenticated === null ? (
            <h3
              onClick={handleLoginButton}
              className={`${
                type === "login" || type === "signup"
                  ? "hidden"
                  : "inline-block"
              } px-2 py-1 transition-all ease-in-out font-bold text-gray-700 border border-gray-400 rounded-md cursor-pointer hover:border-white duration-800 hover:text-gray-700 hover:bg-[#DFDFDF]`}
            >
              Login
            </h3>
          ) : (
            <>
              {isDashboardRoute && (
                <>
                  <div className="flex items-center justify-center gap-1 text-gray-700 hover:text-gray-900">
                    <FaPlus size={16} onClick={handleAddlistModalToggle} />
                  </div>

                  {!selectedStore?.isWhiteLabel && (
                    <div className="flex items-center justify-center gap-1 text-gray-700 hover:text-gray-900">
                      <MdOutlinePeopleAlt
                        onClick={handleMembersModalToggle}
                        size={20}
                      />
                      {!membersModalOpen && friendRequestsCount !== 0 && (
                        <span
                          className="absolute flex items-center justify-center w-3 h-3 px-2 py-2 font-bold text-white translate-x-1/2 bg-red-500 border border-white rounded-full cursor-pointer top-3"
                          style={{ fontSize: "12px" }}
                        >
                          {friendRequestsCount > 0 && friendRequestsCount}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="relative inline-block notification-icon">
                    <div className="drop-shadow-md">
                      <div
                        className={`relative inline-block mt-1 text-2xl text-gray-700 hover:text-gray-900 md:inline-block ${
                          isNotificationBarOpen ? "text-gray-900" : ""
                        }`}
                      >
                        <TbBellFilled
                          size={18}
                          onClick={handleNotificationBarToggle}
                        />

                        {!isNotificationBarOpen && notificationCount !== 0 && (
                          <span
                            className="absolute right-0 flex items-center justify-center w-3 h-3 px-2 py-2 font-bold text-white translate-x-1/2 bg-red-500 border border-white rounded-full cursor-pointer left-1 bottom-3"
                            style={{ fontSize: "12px" }}
                          >
                            {notificationCount > 0 && notificationCount}
                          </span>
                        )}
                      </div>
                    </div>
                    {isNotificationBarOpen && (
                      <div className="absolute right-0 p-2 bg-white rounded shadow-md sm:mr-56 top-6 lg:-right-52 lg:top-6 md:-right-52 md:top-7 ">
                        <Notifications />
                      </div>
                    )}
                  </div>

                  <button
                    data-collapse-toggle="navbar-user"
                    type="button"
                    className="inline-flex items-center justify-center w-10 h-10 p-2 text-sm text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
                    aria-controls="navbar-user"
                    aria-expanded={isNavbarOpen}
                    onClick={() => setIsNavbarOpen(!isNavbarOpen)}
                  >
                    <span className="sr-only">Open main menu</span>
                    <OpenMenuButton />
                  </button>
                </>
              )}
              {isDashboardRoute === false && (
                <button
                  data-collapse-toggle="navbar-user"
                  type="button"
                  className="inline-flex items-center justify-center w-10 h-10 p-2 text-sm text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
                  aria-controls="navbar-user"
                  aria-expanded={isNavbarOpen}
                  onClick={() => setIsNavbarOpen(!isNavbarOpen)}
                >
                  <span className="sr-only">Open main menu</span>
                  <OpenMenuButton />
                </button>
              )}
            </>
          )}
        </div>
        {isAuthenticated !== null && (
          <div
            className={`items-center justify-start w-full md:flex md:w-auto md:order-1 
            ${isNavbarOpen ? "block" : "hidden"} ${
              !isDashboardRoute && !selectedStore?.isWhiteLabel
                ? "sm:mr-0 md:mr-20 lg:mr-24 "
                : "lg:mr-2 mr-0 md:mr-0"
            }
            ${
              !isDashboardRoute && selectedStore?.isWhiteLabel
                ? "sm:mr-0 md:mr-16 lg:mr-28 "
                : "lg:-ml-12 mr-0 md:-ml-4"
            } `}
            id="navbar-user"
          >
            <ul className="flex flex-col p-4 mt-4 font-medium border border-gray-100 rounded-lg md:p-0 bg-gray-50 md:space-x-8 rtl:space-x-reverse md:flex-row md:mt-0 md:border-0 md:bg-white dark:bg-gray-800 md:dark:bg-gray-900 dark:border-gray-700">
              {navbarItems.map((item) => (
                <NavItem
                  key={item.text}
                  to={item.to}
                  text={item.text}
                  isActive={item.isActive}
                  onClick={() => handleNavItemClick(item)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
      <AddList
        selectedStore={selectedStore && selectedStore}
        isOpen={addListModalOpen}
        handleClose={handleAddlistModalToggle}
      />
      <Friends
        isOpen={membersModalOpen}
        handleClose={handleMembersModalToggle}
      />
      {isMessageModalOpen && !selectedStore?.isWhiteLabel && (
        <NofificationModal
          modalData={modalContent}
          handleModalToggle={handleMessageModalToggle}
          isOpen={isMessageModalOpen}
        />
      )}
    </nav>
  );
};

export default Navbar;
